import { useEffect, useState } from 'react'
import type { MatchedHeaderCategory } from './useHeaderCategories'

// Layout constants kept local to the hook
const CATEGORY_CHIP_BASE_PX = 28
const CATEGORY_CHIP_CHAR_PX = 8
const CATEGORY_CHIP_MAX_PX = 360
const CATEGORY_CHIP_GAP_PX = 16
const MORE_BUTTON_PX = 176
const CENTER_SAFETY_PADDING_PX = 24

function estimateChipWidth(label: string) {
  const estimated = CATEGORY_CHIP_BASE_PX + label.length * CATEGORY_CHIP_CHAR_PX
  return Math.min(estimated, CATEGORY_CHIP_MAX_PX)
}

/**
 * Calculate how many categories can fit within the element width.
 */
export function useCategoryFit(
  isCityPage: boolean,
  matchedHeaderCategories: MatchedHeaderCategory[],
  centerEl: HTMLElement | null,
) {
  const [numVisibleCategories, setNumVisibleCategories] = useState<number>(0)

  useEffect(() => {
    if (!isCityPage || !centerEl) return

    const compute = () => {
      let measured = centerEl.clientWidth || 0
      if (measured <= 0) measured = centerEl.getBoundingClientRect()?.width || 0
      if (measured <= 0) measured = centerEl.parentElement?.clientWidth || 0
      if (measured <= 0) measured = Math.max(0, Math.floor(window.innerWidth * 0.5))
      const totalWidth = measured - CENTER_SAFETY_PADDING_PX
      if (totalWidth <= 0 || matchedHeaderCategories.length === 0) {
        setNumVisibleCategories(0)
        return
      }

      const widths = matchedHeaderCategories.map((c) => estimateChipWidth(c.label))

      const fitCount = (available: number) => {
        let used = 0
        let count = 0
        for (let i = 0; i < widths.length; i++) {
          const w = widths[i]
          const add = w + (i === 0 ? 0 : CATEGORY_CHIP_GAP_PX)
          if (used + add > available) break
          used += add
          count += 1
        }
        return count
      }

      const countWithoutReservation = fitCount(totalWidth)
      let count = countWithoutReservation
      if (count < widths.length) {
        const withMoreReserved = Math.max(0, totalWidth - MORE_BUTTON_PX - CATEGORY_CHIP_GAP_PX)
        const countWithReservation = fitCount(withMoreReserved)
        count = countWithReservation > 0 ? countWithReservation : countWithoutReservation
      }
      if (count === 0 && widths.length > 0 && widths[0] <= totalWidth) {
        count = 1
      }
      setNumVisibleCategories(count)
    }

    // Run now, but schedule to next frame to ensure layout is measured after paint
    const rafId = requestAnimationFrame(() => compute())
    // Recompute on resize via ResizeObserver if supported, else window resize
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => compute())
      ro.observe(centerEl)
    } else {
      const onResize = () => compute()
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }

    return () => {
      cancelAnimationFrame(rafId)
      if (ro) ro.disconnect()
    }
  }, [isCityPage, matchedHeaderCategories, centerEl])

  return numVisibleCategories
}


